import { config } from "../config";
import { TSession } from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefixDocumentation = `${protocol}://${host}/clinics/v1/documentation`;

// Types for patient summary generation
export type TPatientAndAppointmentInfo = {
  patient_uuid: string;
  appointment_id: number;
};

export type TGeneratePatientSummariesRequest = {
  patients_info: TPatientAndAppointmentInfo[];
  update_existing_summaries?: boolean;
};

export type TGeneratePatientSummariesResponse = {
  status: string;
  message: string;
  clinic_id: string;
  patient_count: number;
  task_id: number;
  update_existing_summaries: boolean;
};

// Types for status polling
export type TBackgroundTaskStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed";

export type TSuccessfulSummary = {
  patient_uuid: string;
  appointment_id: number;
  summary_generated: boolean;
  updated_existing: boolean;
};

export type TFailedSummary = {
  patient_uuid: string;
  appointment_id: number;
  error: string;
};

export type TSkippedSummary = {
  patient_uuid: string;
  appointment_id: number;
  reason: string;
};

export type TPatientSummaryResult = {
  status: string;
  total_patients: number;
  successful_count: number;
  failed_count: number;
  skipped_count: number;
  successful_summaries: TSuccessfulSummary[];
  failed_summaries: TFailedSummary[];
  skipped_summaries: TSkippedSummary[];
  update_existing_summaries: boolean;
};

export type TPatientSummaryStatusResponse = {
  id: number;
  clinic_uuid: string;
  task_name: string;
  status: TBackgroundTaskStatus;
  result: TPatientSummaryResult | null;
  created_at: string;
  updated_at: string;
};

// Generate patient summaries
export const generatePatientSummaries = async (
  session: TSession,
  clinicId: string,
  payload: TGeneratePatientSummariesRequest,
): Promise<TGeneratePatientSummariesResponse> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/generate-patient-summaries`;

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to generate patient summaries: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

// Get patient summary generation status
export const getPatientSummaryStatus = async (
  session: TSession,
  clinicId: string,
  taskId: number,
): Promise<TPatientSummaryStatusResponse> => {
  const url = `${urlPrefixDocumentation}/${clinicId}/patient-summaries-status/${taskId}`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch patient summary status: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

// Polling configuration
export type TPollingOptions = {
  intervalMs?: number; // Polling interval in milliseconds (default: 2000)
  maxAttempts?: number; // Maximum polling attempts (default: 30)
  onProgress?: (status: TPatientSummaryStatusResponse) => void; // Callback for progress updates
};

// Poll patient summary status until completion
export const pollPatientSummaryStatus = async (
  session: TSession,
  clinicId: string,
  taskId: number,
  options: TPollingOptions = {},
): Promise<TPatientSummaryStatusResponse> => {
  const { intervalMs = 2000, maxAttempts = 30, onProgress } = options;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const status = await getPatientSummaryStatus(session, clinicId, taskId);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }

      // Check if task is completed (success or failure)
      if (status.status === "completed" || status.status === "failed") {
        return status;
      }

      // Wait before next poll (except on last attempt)
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    } catch (error) {
      // On error, wait and retry unless it's the last attempt
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  throw new Error(
    `Patient summary generation polling timed out after ${maxAttempts} attempts`,
  );
};

// Convenience function to generate summaries and wait for completion
export const generatePatientSummariesAndWait = async (
  session: TSession,
  clinicId: string,
  payload: TGeneratePatientSummariesRequest,
  pollingOptions: TPollingOptions = {},
): Promise<{
  generation: TGeneratePatientSummariesResponse;
  completion: TPatientSummaryStatusResponse;
}> => {
  // Start the generation
  const generation = await generatePatientSummaries(session, clinicId, payload);

  // Poll until completion
  const completion = await pollPatientSummaryStatus(
    session,
    clinicId,
    generation.task_id,
    pollingOptions,
  );

  return { generation, completion };
};
